import React, { useRef } from 'react';
import { createPortal } from 'react-dom';
import { FileDown, FileText, Printer, X } from 'lucide-react';
import { formatClinicDate } from '../utils/clinicTime';
import { printNode } from '../utils/printDocument';
import { savePrescriptionPdf } from '../utils/pdfDocument';
import usePdfDownload from '../hooks/usePdfDownload';
import useDialog from '../hooks/useDialog';
import PrescriptionPrint from './PrescriptionPrint';

// Read-only prescription "view" — the canonical detail/print view, reused everywhere a
// prescription is opened from a list/history (the Prescription module, the patient details
// modal's Prescriptions tab, etc.). Pass a FULLY-loaded prescription (e.g. prescriptionAPI.getById).
const PrescriptionDetailModal = ({ prescription, onClose }) => {
  const printRef = useRef();
  const prescriptionDialogRef = useDialog(onClose, { confirmDiscard: false });
  const [downloading, download] = usePdfDownload();
  const handleDownload = () => download(() => savePrescriptionPdf(printRef.current, {
    title: `Prescription ${prescription?.prescriptionNumber || ''}`.trim(),
    fileName: prescription?.prescriptionNumber || 'Prescription',
  }));

  const handlePrint = () => {
    printNode(printRef.current, `Prescription - ${prescription?.prescriptionNumber}`, {
      // Close this preview modal after the print dialog returns so the user doesn't have
      // to click the X separately.
      onAfterPrint: () => { if (onClose) onClose(); },
    });
  };

  // Rendered through a portal to document.body so it always covers the full viewport and stacks
  // above other modals — when opened from inside another modal (e.g. the patient details modal),
  // an ancestor's stacking/transform would otherwise confine this `fixed` overlay to that modal.
  return createPortal(
    <div ref={prescriptionDialogRef} className="fixed inset-0 bg-black/60 flex items-center justify-center z-[70] p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[95vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold flex items-center">
            <FileText className="w-5 h-5 mr-2 text-emerald-600" />
            {prescription.prescriptionNumber || 'Prescription Details'}
            {prescription.createdAt && (
              <span className="ml-2 text-sm font-normal text-gray-500">
                · {formatClinicDate(prescription.createdAt, { day: 'numeric', month: 'short', year: 'numeric' })}
              </span>
            )}
          </h3>
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={handleDownload}
              disabled={downloading}
              className="flex items-center space-x-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              <FileDown className="w-4 h-4" />
              <span>{downloading ? 'Making the PDF…' : 'Download PDF'}</span>
            </button>
            <button
              onClick={handlePrint}
              className="flex items-center space-x-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
            >
              <Printer className="w-4 h-4" />
              <span>Print</span>
            </button>
            <button
              aria-label="Close"
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-auto p-6 bg-gray-100">
          <div className="mx-auto" style={{ maxWidth: '210mm' }}>
            <PrescriptionPrint
              ref={printRef}
              prescription={prescription}
              patient={prescription.patient}
              doctor={prescription.doctor}
            />
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default PrescriptionDetailModal;
